import { computed, ref, watch } from 'vue'
import { VIP_TYPE } from '@/constants'
import { useUserStore } from '@/stores'
import { api } from '@/api'
import { createDebugger } from '@/utils/debug'
import { features } from '@/config/brands'
import { clearRetentionCheckoutPending, readRetentionCheckoutPending } from '@/utils/retentionCheckoutStorage'
import type { MemberTopupSubscribeProduct } from '@/composables/payment'

const DEFAULT_COUNTDOWN_SECONDS = 30 * 60
const configuredCountdownSeconds = features?.retentionSubscriptionPopupCountdownSeconds
const countdownDurationSeconds = configuredCountdownSeconds && configuredCountdownSeconds > 0
  ? configuredCountdownSeconds
  : DEFAULT_COUNTDOWN_SECONDS
const LS_EXPIRE_AT = 'rs_popup_expire_at'
const LS_FLOATING = 'rs_floating_shown'
const LS_PRODUCT = 'rs_popup_product'
const LS_BALANCE_UPDATE_ARMED = 'rs_balance_update_armed'
const debug = createDebugger('retention-popup')

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  }
  catch {
    return null
  }
}

function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  }
  catch {}
}

function safeRemove(key: string) {
  try {
    localStorage.removeItem(key)
  }
  catch {}
}

function hasActiveSession(): boolean {
  const expireAt = safeGet(LS_EXPIRE_AT)
  return !!expireAt && Date.now() <= Number(expireAt)
}

// Module-level singleton state (shared across all components)
const popupVisible = ref(false)
const floatingVisible = ref(safeGet(LS_FLOATING) === 'true' && hasActiveSession())
const floatingHiddenWhileScrolling = ref(false)
const countdown = ref(0)
const showCloseButton = ref(false)

// 选中的挽留档位需持久化：支付页返回阅读器可能触发整页重载，
// 内存中的 singleton 会被重置为 null，导致悬浮窗再次打开弹窗时无档位。
function readStoredProduct(): MemberTopupSubscribeProduct | null {
  try {
    const raw = safeGet(LS_PRODUCT)
    return raw ? JSON.parse(raw) : null
  }
  catch { return null }
}

const selectedProduct = ref<MemberTopupSubscribeProduct | null>(readStoredProduct())

watch(selectedProduct, (val) => {
  if (val)
    safeSet(LS_PRODUCT, JSON.stringify(val))
  else
    safeRemove(LS_PRODUCT)
})

let countdownTimer: number | null = null
let floatingScrollTimer: number | null = null
let _userStore: ReturnType<typeof useUserStore> | null = null
let _pendingLeaveNext: (() => void) | null = null
// 点击挽留弹窗的下单动作：弹窗在 App.vue 全局渲染、无 book 上下文，
// 由阅读器页面（持有 book）注册具体下单逻辑，App.vue 的 action 委托调用
type RetentionActionResult = boolean | Promise<boolean>
let _actionHandler: ((product: MemberTopupSubscribeProduct | null) => RetentionActionResult) | null = null

// Restore countdown from previous session
if (hasActiveSession()) {
  updateCountdown()
  countdownTimer = window.setInterval(updateCountdown, 1000)
}

export function setPendingLeaveNext(fn: (() => void) | null) {
  _pendingLeaveNext = fn
}

// 返回清理函数：仅当当前 handler 仍是自己时才清空，避免路由切换时
// 旧页 onUnmounted 误清掉新页刚注册的 handler
export function setRetentionActionHandler(fn: (product: MemberTopupSubscribeProduct | null) => RetentionActionResult) {
  _actionHandler = fn
  return () => {
    if (_actionHandler === fn)
      _actionHandler = null
  }
}

export function runRetentionAction(product: MemberTopupSubscribeProduct | null) {
  if (!_actionHandler)
    return null

  return _actionHandler(product)
}

/** 阅读器滚动期间暂时隐藏悬浮入口，停止滚动 2 秒后恢复。 */
export function notifyRetentionFloatingScroll() {
  floatingHiddenWhileScrolling.value = true
  if (floatingScrollTimer !== null)
    clearTimeout(floatingScrollTimer)

  floatingScrollTimer = window.setTimeout(() => {
    floatingHiddenWhileScrolling.value = false
    floatingScrollTimer = null
  }, 2000)
}

export function resetRetentionFloatingScrollState() {
  if (floatingScrollTimer !== null) {
    clearTimeout(floatingScrollTimer)
    floatingScrollTimer = null
  }
  floatingHiddenWhileScrolling.value = false
}

export function resumePendingLeaveAfterClose() {
  if (_pendingLeaveNext) {
    const fn = _pendingLeaveNext
    _pendingLeaveNext = null
    setTimeout(fn, 100)
  }
}
function getUserStore() {
  if (!_userStore)
    _userStore = useUserStore()
  return _userStore
}

function isNonReadingVip(): boolean {
  const userStore = getUserStore()
  if (!userStore.$state.subscribe)
    return true
  if (userStore.$state.subscribeType !== VIP_TYPE.ALL)
    return true
  if (userStore.$state.vipEndTime && new Date(userStore.$state.vipEndTime) < new Date())
    return true
  return false
}

/** 是否存在有效的悬浮会话；滚动隐藏只控制展示，不影响业务去重。 */
function hasActiveFloatingSession(): boolean {
  return floatingVisible.value && countdown.value > 0
}

const countdownText = computed(() => {
  const totalSec = countdown.value
  if (totalSec <= 0)
    return '00 : 00'
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${String(min).padStart(2, '0')} : ${String(sec).padStart(2, '0')}`
})

function updateCountdown() {
  const expireAt = safeGet(LS_EXPIRE_AT)
  if (!expireAt) {
    countdown.value = 0
    return
  }
  const remaining = Math.max(0, Math.floor((Number(expireAt) - Date.now()) / 1000))
  countdown.value = remaining
  if (remaining <= 0) {
    stopCountdown()
    popupVisible.value = false
    floatingVisible.value = false
    showCloseButton.value = false
    selectedProduct.value = null
    safeRemove(LS_EXPIRE_AT)
    safeRemove(LS_FLOATING)
    safeRemove(LS_BALANCE_UPDATE_ARMED)
    clearPendingPaymentCheck()
    resumePendingLeaveAfterClose()
  }
}

function startCountdown() {
  stopCountdown()
  const expireAt = Date.now() + countdownDurationSeconds * 1000
  safeSet(LS_EXPIRE_AT, String(expireAt))
  updateCountdown()
  countdownTimer = window.setInterval(updateCountdown, 1000)
}

function stopCountdown() {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function showPopup(restartCountdown = true) {
  debug('show popup %o', {
    restartCountdown,
    countdown: countdown.value,
    hasProduct: !!selectedProduct.value,
  })
  popupVisible.value = true
  floatingVisible.value = false
  safeRemove(LS_FLOATING)
  showCloseButton.value = false
  setTimeout(() => {
    showCloseButton.value = true
  }, 1000)
  if (restartCountdown)
    startCountdown()
}

/** 支付未完成返回时恢复弹窗：已有会话沿用剩余时间，首次触发才启动 30 分钟。 */
function restorePopup() {
  debug('restore popup %o', {
    countdown: countdown.value,
    restartCountdown: countdown.value <= 0,
    hasProduct: !!selectedProduct.value,
  })
  showPopup(countdown.value <= 0)
}

function hidePopup() {
  debug('hide popup and show floating entry %o', { countdown: countdown.value })
  popupVisible.value = false
  showCloseButton.value = false
  floatingVisible.value = true
  safeSet(LS_FLOATING, 'true')
  resumePendingLeaveAfterClose()
}

function dismissPopup() {
  debug('dismiss popup session')
  popupVisible.value = false
  floatingVisible.value = false
  showCloseButton.value = false
  stopCountdown()
  safeRemove(LS_EXPIRE_AT)
  safeRemove(LS_FLOATING)
  safeRemove(LS_PRODUCT)
  safeRemove(LS_BALANCE_UPDATE_ARMED)
  clearPendingPaymentCheck()
}

function clearPendingPaymentCheck() {
  debug('clear pending payment marker')
  clearRetentionCheckoutPending()
}

function shouldTriggerOnExit(balance: number, currentChapterIsPaid: boolean, nextChapterIsPaid: boolean, nextChapterPrice: number | undefined): boolean {
  const nonReadingVip = isNonReadingVip()
  const activePopup = popupVisible.value
  const activeFloatingSession = hasActiveFloatingSession()

  const logExitDecision = (reason: string, result: boolean) => {
    debug('exit decision %o', {
      result,
      reason,
      balance,
      currentChapterIsPaid,
      nextChapterIsPaid,
      nextChapterPrice,
      nonReadingVip,
      activePopup,
      activeFloatingSession,
    })
    return result
  }

  if (!nonReadingVip)
    return logExitDecision('active-all-vip', false)

  if (activePopup)
    return logExitDecision('popup-already-visible', false)

  if (activeFloatingSession)
    return logExitDecision('floating-entry-visible', false)

  // 价格会因章节已解锁而变为 0，收费属性必须以 feeType 为准。
  // 当前章、下一章都必须是收费章节；不关心当前章是否已经解锁。
  if (!currentChapterIsPaid || !nextChapterIsPaid)
    return logExitDecision('current-or-next-chapter-is-free-or-unresolved', false)

  if (balance <= 100)
    return logExitDecision('balance-at-most-100', true)

  if (nextChapterPrice !== undefined && nextChapterPrice > 0 && balance < nextChapterPrice)
    return logExitDecision('balance-lower-than-next-chapter-price', true)

  return logExitDecision('balance-sufficient', false)
}

async function shouldTriggerOnBalanceUpdate(): Promise<boolean> {
  if (!isNonReadingVip())
    return false
  if (popupVisible.value)
    return false
  if (hasActiveFloatingSession())
    return false

  try {
    const { data } = await api.get<any>('/member/subscribe/retain/eligible')
    if (data !== true) {
      safeRemove(LS_BALANCE_UPDATE_ARMED)
      return false
    }

    if (safeGet(LS_BALANCE_UPDATE_ARMED) === 'true') {
      safeRemove(LS_BALANCE_UPDATE_ARMED)
      return true
    }

    safeSet(LS_BALANCE_UPDATE_ARMED, 'true')
    return false
  }
  catch {}

  return false
}

/** 用户取消外部支付后，当前用户与弹窗状态是否允许恢复挽留弹窗。 */
function shouldRestoreOnCheckoutCancel(): boolean {
  const nonReadingVip = isNonReadingVip()
  const pendingPayment = readRetentionCheckoutPending()
  const activeFloatingSession = hasActiveFloatingSession()
  const logCheckoutCancelDecision = (reason: string, result: boolean) => {
    debug('checkout cancel decision %o', {
      result,
      reason,
      pendingPayment,
      nonReadingVip,
      popupVisible: popupVisible.value,
      activeFloatingSession,
      countdown: countdown.value,
      hasProduct: !!selectedProduct.value,
    })
    return result
  }

  // 已成为全站阅读 VIP（如支付成功）：挽留会话整体作废，清空全部状态并隐藏弹窗。
  if (!nonReadingVip) {
    dismissPopup()
    return logCheckoutCancelDecision('active-all-vip', false)
  }

  if (activeFloatingSession) {
    clearPendingPaymentCheck()
    return logCheckoutCancelDecision('floating-entry-already-visible', false)
  }

  if (pendingPayment !== 'true')
    return logCheckoutCancelDecision('pending-marker-missing', false)

  return logCheckoutCancelDecision('eligible', true)
}

export function useRetentionSubscriptionPopup() {
  return {
    popupVisible,
    floatingVisible,
    countdown,
    countdownText,
    showCloseButton,
    selectedProduct,
    shouldTriggerOnExit,
    shouldTriggerOnBalanceUpdate,
    shouldRestoreOnCheckoutCancel,
    clearPendingPaymentCheck,
    showPopup,
    restorePopup,
    hidePopup,
    dismissPopup,
    startCountdown,
    stopCountdown,
  }
}

export function useRetentionFloatingState() {
  return {
    popupVisible,
    floatingVisible,
    floatingHiddenWhileScrolling,
    countdown,
    countdownText,
    showCloseButton,
    selectedProduct,
    showPopup,
    restorePopup,
    hidePopup,
    dismissPopup,
    shouldRestoreOnCheckoutCancel,
    clearPendingPaymentCheck,
  }
}
